import { MediaDisplay } from './MediaDisplay';

/**
 * SnapchatMockup - Snapchat 9:16 Ad Mockup
 *
 * Note: imageUrl prop can contain either image or video URLs
 */

interface SnapchatMockupProps {
  username?: string;
  profilePicUrl?: string;
  imageUrl?: string;
  contentType?: string | null;
  headline?: string;
}

export function SnapchatMockup({
  username = 'Ford',
  profilePicUrl,
  imageUrl,
  contentType,
  headline = '',
}: SnapchatMockupProps) {
  return (
    <div className="relative mx-auto" style={{ width: '360px', height: '640px', background: '#000' }}>
      {/* Header */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Profile Picture */}
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            overflow: 'hidden',
            background: '#555',
            border: '2px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {profilePicUrl ? (
              <img src={profilePicUrl} alt={username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: '#fff', fontWeight: 'bold' }}>{username.charAt(0).toUpperCase()}</span>
            )}
          </div>
          {/* Username & Sponsored */}
          <div>
            <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: 600, margin: 0 }}>{username}</h4>
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>Sponsored</span>
          </div>
        </div>
        {/* Three Dots Menu */}
        <div>
          <svg width="6" height="21" viewBox="0 0 6 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="3" cy="3.50001" rx="3" ry="3.00001" fill="#F9F9F9"/>
            <ellipse cx="3" cy="10.5" rx="3" ry="3.00001" fill="#F9F9F9"/>
            <ellipse cx="3" cy="17.5" rx="3" ry="3.00001" fill="#F9F9F9"/>
          </svg>
        </div>
      </div>

      {/* Body - Background Image/Video */}
      <div style={{ position: 'absolute', inset: 0 }}>
        {imageUrl ? (
          <MediaDisplay url={imageUrl} contentType={contentType} alt="Snapchat ad" className="w-full h-full object-cover" />
        ) : (
          <div style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
              <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '8px' }}>9:16</div>
              <div style={{ fontSize: '14px' }}>1080x1920px recommended</div>
            </div>
          </div>
        )}
      </div>

      {/* Footer - Grey info box (left) and Ad badge (right) on same row */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 20,
        padding: '16px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '12px'
      }}>
        {/* Grey Info Box - left side */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: 'rgba(80, 80, 80, 0.6)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderRadius: '12px',
          padding: '10px 12px',
          flex: 1,
          minWidth: 0,
          maxWidth: 'calc(100% - 60px)'
        }}>
          {/* Brand info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: '2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {username}
            </div>
            {headline && (
              <div style={{
                color: '#fff',
                fontSize: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {headline}
              </div>
            )}
          </div>
          {/* Chevron arrow */}
          <div style={{
            marginLeft: '10px',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Ad Badge - right side */}
        <div style={{
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          padding: '6px 14px',
          borderRadius: '9999px',
          color: '#fff',
          fontSize: '13px',
          fontWeight: 600,
          flexShrink: 0
        }}>
          Ad
        </div>
      </div>
    </div>
  );
}
